import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

const CONVERSATIONS_PAGE_SIZE = 100;
const MESSAGES_PAGE_SIZE = 500;
const MESSAGE_SELECT = "from_user, body, created_at";

const CONVERSATION_SELECT = `
  id,
  channel,
  created_at,
  external_user_id,
  lead:lead_id (
    name,
    phone
  )
`;

const MAX_EXPORT_PAGES = 500;

const CHANNEL_ORDER = ["whatsapp", "instagram", "messenger"];

export const CHANNEL_EXPORT_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  messenger: "Messenger",
};

export type ExportConversationMessage = {
  from_user: string;
  body: string | null;
  created_at: string | null;
};

export type ExportConversation = {
  id: string;
  external_user_id: string;
  lead: {
    name: string | null;
    phone: string | null;
  } | null;
  messages: ExportConversationMessage[];
};

export type ExportChannelBucket = {
  channel: string;
  label: string;
  conversations: ExportConversation[];
};

export type WorkspaceConversationsExport = {
  exported_at: string;
  workspace_id: string;
  channels: Record<string, ExportChannelBucket>;
  stats: {
    total_conversations: number;
    total_messages: number;
    by_channel: Record<string, { conversations: number; messages: number }>;
  };
};

export function channelExportLabel(channel: string): string {
  return CHANNEL_EXPORT_LABELS[channel] ?? channel;
}

export function createEmptyExport(workspaceId: string): WorkspaceConversationsExport {
  return {
    exported_at: new Date().toISOString(),
    workspace_id: workspaceId,
    channels: {},
    stats: {
      total_conversations: 0,
      total_messages: 0,
      by_channel: {},
    },
  };
}

export function ensureChannelBucket(
  exportData: WorkspaceConversationsExport,
  channel: string,
): ExportChannelBucket {
  if (!exportData.channels[channel]) {
    exportData.channels[channel] = {
      channel,
      label: channelExportLabel(channel),
      conversations: [],
    };
  }
  return exportData.channels[channel];
}

export function addConversationToExport(
  exportData: WorkspaceConversationsExport,
  channel: string,
  conversation: ExportConversation,
): void {
  const bucket = ensureChannelBucket(exportData, channel);
  bucket.conversations.push(conversation);

  exportData.stats.total_conversations += 1;
  exportData.stats.total_messages += conversation.messages.length;

  if (!exportData.stats.by_channel[channel]) {
    exportData.stats.by_channel[channel] = { conversations: 0, messages: 0 };
  }
  exportData.stats.by_channel[channel].conversations += 1;
  exportData.stats.by_channel[channel].messages += conversation.messages.length;
}

export function sanitizeExportMessage(row: Record<string, unknown>): ExportConversationMessage {
  return {
    from_user: String(row.from_user ?? ""),
    body: row.body != null ? String(row.body) : null,
    created_at: row.created_at != null ? String(row.created_at) : null,
  };
}

export function mapConversationRow(row: Record<string, unknown>): Omit<ExportConversation, "messages"> {
  const leadRaw = row.lead as Record<string, unknown> | null | undefined;
  return {
    id: String(row.id),
    external_user_id: String(row.external_user_id ?? ""),
    lead: leadRaw
      ? {
          name: leadRaw.name != null ? String(leadRaw.name) : null,
          phone: leadRaw.phone != null ? String(leadRaw.phone) : null,
        }
      : null,
  };
}

export function resolveClientName(
  conversation: Pick<ExportConversation, "lead" | "external_user_id">,
): string {
  const name = conversation.lead?.name?.trim();
  if (name) return name;

  const phone = conversation.lead?.phone?.trim();
  if (phone) return phone;

  const external = conversation.external_user_id.trim();
  if (external) return external;

  return "Cliente";
}

export function isOutboundMessage(fromUser: string): boolean {
  return fromUser === "assistant" || fromUser === "me";
}

export function resolveMessageSender(fromUser: string, clientName: string): string {
  if (isOutboundMessage(fromUser)) return "Atendente";
  if (clientName !== "Cliente") return clientName;
  return "Cliente";
}

export function formatExportTimestamp(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
}

function normalizeMessageBody(body: string | null): string {
  if (!body?.trim()) return "(sem texto)";
  return body.replace(/\s+/g, " ").trim();
}

function sortChannelKeys(channels: Record<string, ExportChannelBucket>): string[] {
  return Object.keys(channels).sort((a, b) => {
    const ai = CHANNEL_ORDER.indexOf(a);
    const bi = CHANNEL_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return channels[a].label.localeCompare(channels[b].label, "pt-BR");
  });
}

export function formatConversationsExportText(
  exportData: WorkspaceConversationsExport,
  workspaceName?: string,
): string {
  const lines: string[] = [];
  const title = workspaceName?.trim() || "Workspace";
  const exportedAt = formatExportTimestamp(exportData.exported_at);

  lines.push(`Exportação de conversas — ${title}`);
  lines.push(`Exportado em: ${exportedAt}`);
  lines.push("");

  const channelKeys = sortChannelKeys(exportData.channels);

  if (channelKeys.length === 0) {
    lines.push("Nenhuma conversa encontrada.");
  }

  for (const channelKey of channelKeys) {
    const bucket = exportData.channels[channelKey];
    lines.push(`=== ${bucket.label} ===`);
    lines.push("");

    for (const conversation of bucket.conversations) {
      const clientName = resolveClientName(conversation);
      lines.push(`Cliente: ${clientName}`);
      lines.push("---");

      if (conversation.messages.length === 0) {
        lines.push("(sem mensagens)");
      } else {
        for (const message of conversation.messages) {
          const timestamp = formatExportTimestamp(message.created_at);
          const sender = resolveMessageSender(message.from_user, clientName);
          const body = normalizeMessageBody(message.body);
          lines.push(`[${timestamp}] ${sender}: ${body}`);
        }
      }

      lines.push("");
    }
  }

  const { total_conversations, total_messages } = exportData.stats;
  lines.push("---");
  lines.push(`Resumo: ${total_conversations} conversas, ${total_messages} mensagens`);

  return lines.join("\n");
}

export type ConversationsExportProgress = {
  phase: "conversations" | "messages" | "finalizing";
  completed: number;
  total: number;
  currentChannel?: string;
};

export function postgrestQuotedLiteral(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

export function buildConversationKeysetOrFilter(
  cursorCreatedAt: string,
  cursorId: string,
): string {
  const createdAt = postgrestQuotedLiteral(cursorCreatedAt);
  const id = postgrestQuotedLiteral(cursorId);
  return `created_at.gt.${createdAt},and(created_at.eq.${createdAt},id.gt.${id})`;
}

export function buildConversationNullCreatedAtKeysetOrFilter(cursorId: string): string {
  const id = postgrestQuotedLiteral(cursorId);
  return `created_at.not.is.null,and(created_at.is.null,id.gt.${id})`;
}

export function buildConversationPaginationOrFilter(
  cursorCreatedAt: string | null,
  cursorId: string,
): string {
  if (cursorCreatedAt != null) {
    return buildConversationKeysetOrFilter(cursorCreatedAt, cursorId);
  }
  return buildConversationNullCreatedAtKeysetOrFilter(cursorId);
}

export function conversationPaginationCursorFromRow(
  row: Record<string, unknown>,
): { createdAt: string | null; id: string } {
  return {
    createdAt: row.created_at != null ? String(row.created_at) : null,
    id: String(row.id),
  };
}

export function conversationsExportProgressPercent(progress: {
  completed: number;
  total: number;
}): number {
  if (progress.total <= 0) return 0;
  return Math.min(100, Math.round((progress.completed / progress.total) * 100));
}

async function fetchMessagesForConversation(
  supabase: SupabaseClient<Database>,
  conversationId: string,
): Promise<ExportConversationMessage[]> {
  const messages: ExportConversationMessage[] = [];
  let offset = 0;

  for (;;) {
    const { data, error } = await supabase
      .from("messages")
      .select(MESSAGE_SELECT)
      .eq("conversation_id", conversationId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(offset, offset + MESSAGES_PAGE_SIZE - 1);

    if (error) throw error;

    const rows = data || [];
    if (rows.length === 0) break;

    messages.push(...rows.map((row) => sanitizeExportMessage(row as Record<string, unknown>)));

    if (rows.length < MESSAGES_PAGE_SIZE) break;
    offset += MESSAGES_PAGE_SIZE;
  }

  return messages;
}

export async function exportWorkspaceConversations(
  supabase: SupabaseClient<Database>,
  workspaceId: string,
  onProgress?: (progress: ConversationsExportProgress) => void,
): Promise<WorkspaceConversationsExport> {
  const exportData = createEmptyExport(workspaceId);

  const { count: totalConversations, error: countError } = await supabase
    .from("conversations")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId);

  if (countError) throw countError;

  const total = totalConversations ?? 0;
  let processedConversations = 0;
  let cursorCreatedAt: string | null = null;
  let cursorId: string | null = null;
  const seenConversationIds = new Set<string>();

  onProgress?.({ phase: "conversations", completed: 0, total });

  let pageNum = 0;
  for (;;) {
    if (++pageNum > MAX_EXPORT_PAGES) {
      throw new Error("Exportação interrompida: limite de páginas atingido.");
    }

    let query = supabase
      .from("conversations")
      .select(CONVERSATION_SELECT)
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: true, nullsFirst: true })
      .order("id", { ascending: true })
      .limit(CONVERSATIONS_PAGE_SIZE);

    if (cursorId) {
      query = query.or(buildConversationPaginationOrFilter(cursorCreatedAt, cursorId));
    }

    const { data, error } = await query;
    if (error) throw error;

    const rows = (data || []) as Record<string, unknown>[];
    if (rows.length === 0) break;

    let paginationRepeated = false;

    for (const row of rows) {
      const base = mapConversationRow(row);
      if (seenConversationIds.has(base.id)) {
        paginationRepeated = true;
        break;
      }
      seenConversationIds.add(base.id);

      const channel = String(row.channel ?? "unknown");

      onProgress?.({
        phase: "messages",
        completed: processedConversations,
        total,
        currentChannel: channel,
      });

      const messages = await fetchMessagesForConversation(supabase, base.id);
      addConversationToExport(exportData, channel, { ...base, messages });
      processedConversations += 1;

      onProgress?.({
        phase: "messages",
        completed: processedConversations,
        total,
        currentChannel: channel,
      });
    }

    if (paginationRepeated) break;

    if (rows.length < CONVERSATIONS_PAGE_SIZE) break;

    const last = rows[rows.length - 1];
    const { createdAt: nextCursorCreatedAt, id: nextCursorId } =
      conversationPaginationCursorFromRow(last);

    if (nextCursorCreatedAt === cursorCreatedAt && nextCursorId === cursorId) {
      break;
    }

    cursorCreatedAt = nextCursorCreatedAt;
    cursorId = nextCursorId;
  }

  onProgress?.({
    phase: "finalizing",
    completed: processedConversations,
    total: Math.max(total, processedConversations),
  });
  return exportData;
}

function safeWorkspaceFilename(workspaceName?: string): string {
  return (workspaceName || "workspace")
    .trim()
    .replace(/[^\w\-]+/g, "_")
    .slice(0, 40);
}

export function downloadConversationsExportText(
  exportData: WorkspaceConversationsExport,
  workspaceName?: string,
): void {
  const safeName = safeWorkspaceFilename(workspaceName);
  const date = new Date().toISOString().split("T")[0];
  const content = formatConversationsExportText(exportData, workspaceName);
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `conversas_${safeName}_${date}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
